import { EventPlanOverridesByPlanId } from './eventActivityPlanOverrides'

// A sparse overrides tree holding only the entries that changed, so the server
// can merge it into whatever another editor already saved.
export type EventPlanOverridesPatch = EventPlanOverridesByPlanId

export type PlanOverridesDiff =
  | { kind: 'noop' }
  | { kind: 'patch'; patch: EventPlanOverridesPatch }
  | { kind: 'replace' }

// Paths below a plan id that are replaced wholesale rather than merged field by
// field. Several override values are discriminated unions, so a field-wise
// merge would leave keys from the previous variant behind. '*' matches any key.
// Mirrored by LEAF_PATHS in app/lib/event_plan_overrides_patch.rb.
const LEAF_PATHS: string[][] = [
  ['encounterAssignments', 'abilities', '*', 'hidden'],
  [
    'encounterAssignments',
    'abilities',
    '*',
    'casts',
    '*',
    'boxes',
    '*',
    'entities',
    '*',
  ],
  ['castTargetOverrides', 'casts', '*', 'entities', '*'],
  ['actorOverrides', 'actors', '*'],
  ['visualEntityOverrides', '*'],
]

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const definedKeyCount = (value: Record<string, unknown>) =>
  Object.keys(value).reduce(
    (count, key) => (value[key] === undefined ? count : count + 1),
    0,
  )

// Overrides are persisted as JSON, so an undefined value and a missing key have
// to count as equal or the diff would report changes that can never be saved.
const deepEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true
  if (Array.isArray(left)) {
    if (!Array.isArray(right) || left.length !== right.length) return false
    return left.every((item, index) => deepEqual(item, right[index]))
  }
  if (isPlainObject(left)) {
    if (!isPlainObject(right)) return false
    if (definedKeyCount(left) !== definedKeyCount(right)) return false
    return Object.keys(left).every(
      (key) => left[key] === undefined || deepEqual(left[key], right[key]),
    )
  }
  return false
}

type Leaf = { path: string[]; value: unknown }

const collectLeavesForPath = (
  node: unknown,
  spec: string[],
  path: string[],
  out: Leaf[],
) => {
  if (spec.length === 0) {
    if (node !== undefined) out.push({ path, value: node })
    return
  }
  if (!isPlainObject(node)) return
  const [segment, ...rest] = spec
  if (segment === '*') {
    Object.keys(node).forEach((key) =>
      collectLeavesForPath(node[key], rest, [...path, key], out),
    )
    return
  }
  collectLeavesForPath(node[segment], rest, [...path, segment], out)
}

const collectLeaves = (planEntry: unknown) => {
  const leaves = new Map<string, Leaf>()
  LEAF_PATHS.forEach((spec) => {
    const found: Leaf[] = []
    collectLeavesForPath(planEntry, spec, [], found)
    found.forEach((leaf) => leaves.set(JSON.stringify(leaf.path), leaf))
  })
  return leaves
}

const setAtPath = (
  root: Record<string, unknown>,
  path: string[],
  value: unknown,
) => {
  let node = root
  path.slice(0, -1).forEach((segment) => {
    const existing = node[segment]
    if (!isPlainObject(existing)) node[segment] = {}
    node = node[segment] as Record<string, unknown>
  })
  node[path[path.length - 1]] = value
}

export const applyPlanOverridesPatch = (
  current: EventPlanOverridesByPlanId,
  patch: EventPlanOverridesPatch,
): EventPlanOverridesByPlanId => {
  const result = clone(current) as Record<string, unknown>
  Object.entries(patch).forEach(([planId, planPatch]) => {
    collectLeaves(planPatch).forEach((leaf) => {
      setAtPath(result, [planId, ...leaf.path], clone(leaf.value))
    })
  })
  return result as EventPlanOverridesByPlanId
}

export const diffPlanOverrides = (
  current: EventPlanOverridesByPlanId,
  next: EventPlanOverridesByPlanId,
): PlanOverridesDiff => {
  // Removing an override means clearing a key, which a merge cannot express.
  // Those paths (every "reset" action) send the whole object instead.
  if (Object.keys(current).some((planId) => !(planId in next))) {
    return { kind: 'replace' }
  }

  const patch: Record<string, unknown> = {}
  let changed = false

  for (const planId of Object.keys(next)) {
    const currentLeaves = collectLeaves(current[planId] ?? {})
    const nextLeaves = collectLeaves(next[planId] ?? {})

    for (const key of currentLeaves.keys()) {
      if (!nextLeaves.has(key)) return { kind: 'replace' }
    }

    for (const [key, leaf] of nextLeaves) {
      const previous = currentLeaves.get(key)
      if (previous && deepEqual(previous.value, leaf.value)) continue
      setAtPath(patch, [planId, ...leaf.path], clone(leaf.value))
      changed = true
    }
  }

  // The leaf paths are the only structural knowledge here, so confirm the patch
  // actually reproduces the intended result before trusting it. This has to run
  // even when no leaf changed, otherwise a change the leaf paths do not cover
  // would be reported as a noop and silently dropped.
  const patched = applyPlanOverridesPatch(
    current,
    patch as EventPlanOverridesPatch,
  )
  if (!deepEqual(patched, next)) return { kind: 'replace' }
  if (!changed) return { kind: 'noop' }

  return { kind: 'patch', patch: patch as EventPlanOverridesPatch }
}
